/* Checkout return handler — picks up Stripe's redirect on the way back.
 *
 * The lopettia-auth checkout endpoint sets these defaults for Stripe:
 *   success_url = ${AIBOARD_BASE_URL}/?checkout=success
 *   cancel_url  = ${AIBOARD_BASE_URL}/pricing.html?checkout=cancel
 *
 * Stripe lands the customer on one of those URLs after they pay (or cancel).
 * Without a handler the customer arrives silently, with no clue if it worked.
 * This module reads the ?checkout=… query param, shows a clear toast/banner,
 * refreshes the user state so the new plan + pill propagate, then strips the
 * param from the URL so a refresh doesn't re-toast.
 *
 * Mounted on both index.html (success landing) and pricing.html (cancel
 * landing) — see main.ts and pricing.ts.
 */

import { loadUser } from '../auth';
import { toast } from './toast';

const STORAGE_KEY = 'lp_aiboard_checkout_seen';

function stripCheckoutParam(): void {
  // Remove ?checkout=… cleanly so a manual refresh doesn't replay the toast.
  // History API only — no full reload.
  const url = new URL(window.location.href);
  url.searchParams.delete('checkout');
  url.searchParams.delete('session_id'); // Stripe sometimes appends this
  const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
  window.history.replaceState({}, document.title, cleaned);
}

export async function handleCheckoutReturn(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('checkout');
  if (!status) return;

  // Dedupe: once we've handled a given session_id, don't re-toast on browser
  // back / restored tabs.
  const sessionId = params.get('session_id') || `${status}-${Date.now()}`;
  const seen = sessionStorage.getItem(STORAGE_KEY);
  if (seen === sessionId) {
    stripCheckoutParam();
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, sessionId);

  if (status === 'success') {
    toast('Subscription activated. Welcome to the paid plan.', 6000);
    // Refresh user state so the trial pill flips to the active plan tone and
    // the admin link / feature flags update without a full reload.
    try {
      await loadUser();
    } catch (err) {
      console.warn('post-checkout user refresh failed', err);
    }
  } else if (status === 'cancel') {
    toast('Checkout canceled — your account is unchanged.', 5000);
  } else {
    // Unknown value — be quiet, just clean it from the URL.
  }

  stripCheckoutParam();
}

export function attachCheckoutReturnHandler(): void {
  // Don't block boot. Run on the next tick so toast / header-user have already
  // mounted by the time we fire.
  setTimeout(() => {
    void handleCheckoutReturn();
  }, 0);
}
